// Sub-Account Management API Client
// Handles all API communication with sub-account management backend

import { api as apiClient } from '@/api/index';
import { API_BASE_URL } from '@/config/api.ts';
import { stage } from '@/config/env.ts';

/**
 * Sub-Account API Response Types
 */
export interface SubAccountLink {
  parentUserId: string;
  childUserId: string;
  rootUserId: string;
  accountType: 'ROOT' | 'SUB_ACCOUNT' | 'GRADUATED';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdBy: string;
  creationMethod: 'SELF_REGISTERED' | 'SUB_ACCOUNT';
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubAccountRequest {
  email: string;
}

export interface CreateSubAccountResponse {
  childUserId: string;
  parentUserId: string;
  rootUserId: string;
  accountType: 'SUB_ACCOUNT';
}

export interface GrantAccessRequest {
  role: 'MEMBER' | 'VIEWER';
}

export interface AccountInfo {
  accountType: 'ROOT' | 'SUB_ACCOUNT' | 'GRADUATED';
  rootUserId: string;
  parentUserId?: string;
}

export type CheckUserResponse =
  | {
      exists: true;
      userId: string;
      source: 'cognito';
    }
  | {
      exists: false;
      source: 'none';
    };

export interface InviteUserRequest {
  email: string;
  role: 'MEMBER' | 'VIEWER';
}

export interface InviteUserResponse {
  message: string;
  userId: string;
  orgId: string;
  role: string;
  userCreated: boolean;
}

export interface OrganizationMember {
  userId: string;
  email: string;
  orgId: string;
  role: string;
  type: 'direct' | 'sub-account';
  parentUserId?: string;
}

export interface SubAccountWithAccess {
  childUserId: string;
  parentUserId: string;
  accountType: string;
  status: string;
  createdAt: string;
  organizations: {
    orgId: string;
    role: string;
  }[];
}

export interface AccessManagementView {
  managedOrganizations: {
    orgId: string;
    orgName: string;
    role: string;
    memberCount: number;
  }[];
  subAccounts: SubAccountWithAccess[];
  organizationMembers: OrganizationMember[];
}

/**
 * Sub-Account API Client Class
 */
class SubAccountsAPI {
  /**
   * Get the base URL for sub-account service endpoints
   */
  private get baseURL(): string {
    return API_BASE_URL[stage];
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any,
    config?: any,
  ): Promise<T> {
    try {
      const fullUrl = `${this.baseURL}${endpoint}`;

      const response = await apiClient.request({
        method,
        url: fullUrl,
        data,
        ...config,
      });

      return response.data;
    } catch (error: any) {
      console.error(`❌ Sub-Account API ${method} failed:`, endpoint, error);
      throw error;
    }
  }

  // ============ Sub-Account Management ============

  /**
   * List all sub-accounts for the current user
   */
  async listSubAccounts(): Promise<SubAccountLink[]> {
    const response = await this.request<{ subAccounts: SubAccountLink[]; count: number }>(
      'GET',
      '/sub-accounts',
    );
    return response.subAccounts;
  }

  /**
   * Create a new sub-account
   */
  async createSubAccount(data: CreateSubAccountRequest): Promise<CreateSubAccountResponse> {
    return this.request<CreateSubAccountResponse>('POST', '/sub-accounts', data);
  }

  /**
   * Grant organization access to a sub-account
   */
  async grantOrganizationAccess(
    subAccountId: string,
    orgId: string,
    data: GrantAccessRequest,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'PUT',
      `/sub-accounts/${subAccountId}/organizations/${orgId}`,
      data,
    );
  }

  /**
   * Revoke organization access from a sub-account
   */
  async revokeOrganizationAccess(
    subAccountId: string,
    orgId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'DELETE',
      `/sub-accounts/${subAccountId}/organizations/${orgId}`,
    );
  }

  /**
   * Get current user's account information
   */
  async getAccountInfo(): Promise<AccountInfo> {
    return this.request<AccountInfo>('GET', '/users/me/account-info');
  }

  // ============ User Management ============

  /**
   * Check if a user exists in Cognito by email
   */
  async checkUserExists(email: string): Promise<CheckUserResponse> {
    return this.request<CheckUserResponse>('GET', '/users/check', undefined, {
      params: { email },
    });
  }

  // ============ Organization Invitations ============

  /**
   * Invite a user to an organization
   * Creates Cognito user if needed and adds them to the organization
   */
  async inviteUserToOrganization(
    orgId: string,
    data: InviteUserRequest,
  ): Promise<InviteUserResponse> {
    return this.request<InviteUserResponse>('POST', `/organizations/${orgId}/invites`, data);
  }

  // ============ Access Management ============

  /**
   * Get consolidated access management view
   * Returns:
   * 1. Organizations the current user owns/manages
   * 2. All members with access to those organizations
   * 3. All sub-accounts managed by the current user
   */
  async getAccessManagementView(): Promise<AccessManagementView> {
    return this.request<AccessManagementView>('GET', '/access-management/view');
  }

  /**
   * Remove a member from an organization
   */
  async removeOrganizationMember(
    orgId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'DELETE',
      `/organizations/${orgId}/members/${userId}`,
    );
  }

  /**
   * Update a member's role in an organization
   */
  async updateMemberRole(
    orgId: string,
    userId: string,
    role: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'PUT',
      `/organizations/${orgId}/members/${userId}/role`,
      { role },
    );
  }
}

// Export singleton instance
export const subAccountsAPI = new SubAccountsAPI();
